import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ============================================================================
// TIMEZONE UTILITIES
// Production-level timezone handling for hotel-based time operations
// ============================================================================

export interface TimezoneInfo {
  id: string;           // IANA timezone identifier (e.g., "America/New_York")
  offset: string;       // UTC offset (e.g., "-05:00")
  currentLocalTime: string; // Current local time at the hotel
}

export interface StoreStatus {
  isOpen: boolean;
  nextOpenTime?: string;
}

/**
 * Get the current time in a specific timezone
 * @param timezone - IANA timezone identifier (e.g., "America/New_York")
 * @returns Date-like object with methods to get time components in that timezone
 */
export function getCurrentTimeInTimezone(timezone: string): {
  hours: number;
  minutes: number;
  seconds: number;
  dayOfWeek: string;
  date: Date;
  totalMinutes: number;
  formattedTime: string;
  formattedDate: string;
} {
  const now = new Date();

  // Get the time components in the target timezone
  const options: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
    weekday: 'long',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };

  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(now);

  const getPart = (type: string) => parts.find(p => p.type === type)?.value || '';

  const hours = parseInt(getPart('hour')) || 0;
  const minutes = parseInt(getPart('minute')) || 0;
  const seconds = parseInt(getPart('second')) || 0;
  const dayOfWeek = getPart('weekday');

  return {
    hours,
    minutes,
    seconds,
    dayOfWeek,
    date: now,
    totalMinutes: hours * 60 + minutes,
    formattedTime: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`,
    formattedDate: `${getPart('year')}-${getPart('month')}-${getPart('day')}`,
  };
}

/**
 * Format time from 24h to 12h format with timezone abbreviation
 * @param time - Time string in HH:MM format
 * @param timezone - IANA timezone identifier
 * @returns Formatted time string (e.g., "9:00 AM EST")
 */
export function formatTimeWithTimezone(time: string, timezone: string): string {
  const [hours, minutes] = time.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  const tzAbbr = getTimezoneAbbreviation(timezone);
  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period} ${tzAbbr}`;
}

/**
 * Format time from 24h to 12h format without timezone
 * @param time - Time string in HH:MM format
 * @returns Formatted time string (e.g., "9:00 AM")
 */
export function formatTime12h(time: string): string {
  const [hours, minutes] = time.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
}

/**
 * Get timezone abbreviation from IANA timezone identifier
 * @param timezone - IANA timezone identifier (e.g., "America/New_York")
 * @returns Timezone abbreviation (e.g., "EST", "PST")
 */
export function getTimezoneAbbreviation(timezone: string): string {
  // BC stores run on America/Phoenix for its fixed UTC-7, but are shown as PDT
  if (timezone === 'America/Phoenix') return 'PDT';
  try {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
      timeZoneName: 'short',
    };
    const formatted = new Intl.DateTimeFormat('en-US', options).format(now);
    // Extract timezone abbreviation from formatted string (e.g., "1/15/2026, EST")
    const match = formatted.match(/[A-Z]{2,5}$/);
    return match ? match[0] : timezone.split('/').pop() || 'Local';
  } catch {
    return 'Local';
  }
}

/**
 * Convert time string (HH:MM) to total minutes
 * @param time - Time string in HH:MM format
 * @returns Total minutes from midnight
 */
export function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
}

/**
 * Get date for a specific day offset in a timezone
 * @param timezone - IANA timezone identifier
 * @param daysOffset - Number of days from today (0 = today, 1 = tomorrow, etc.)
 * @returns Object with date information in the specified timezone
 */
export function getDateInTimezone(timezone: string, daysOffset: number = 0): {
  dayOfWeek: string;
  dateString: string;
  formattedDate: string;
  label: string;
} {
  const now = new Date();
  const targetDate = new Date(now.getTime() + daysOffset * 24 * 60 * 60 * 1000);

  const options: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    weekday: 'long',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };

  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(targetDate);

  const getPart = (type: string) => parts.find(p => p.type === type)?.value || '';

  const dayOfWeek = getPart('weekday');
  const month = getPart('month');
  const day = getPart('day');

  // Generate date string in YYYY-MM-DD format
  const isoOptions: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };
  const isoFormatter = new Intl.DateTimeFormat('en-CA', isoOptions); // en-CA gives YYYY-MM-DD format
  const dateString = isoFormatter.format(targetDate);

  let label: string;
  if (daysOffset === 0) {
    label = 'Today';
  } else if (daysOffset === 1) {
    label = 'Tomorrow';
  } else {
    label = dayOfWeek.slice(0, 3); // "Mon", "Tue", etc.
  }

  return {
    dayOfWeek,
    dateString,
    formattedDate: `${month} ${day}`,
    label,
  };
}

/**
 * Get the UTC offset in minutes for a specific timezone at a given date
 * @param timezone - IANA timezone identifier
 * @param date - Date to check offset for (handles DST)
 * @returns Offset in minutes (positive for west of UTC, negative for east)
 */
export function getTimezoneOffsetMinutes(timezone: string, date: Date = new Date()): number {
  // Create formatters for UTC and target timezone
  const utcFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const tzFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  // Parse the formatted dates
  const parseFormattedDate = (formatted: string): Date => {
    // Format: "MM/DD/YYYY, HH:MM:SS"
    const [datePart, timePart] = formatted.split(', ');
    const [month, day, year] = datePart.split('/').map(Number);
    const [hours, minutes, seconds] = timePart.split(':').map(Number);
    return new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));
  };

  const utcDate = parseFormattedDate(utcFormatter.format(date));
  const tzDate = parseFormattedDate(tzFormatter.format(date));

  // Offset = UTC time - Local time (in minutes)
  return (utcDate.getTime() - tzDate.getTime()) / (1000 * 60);
}

/**
 * Convert a date and time to ISO string in a specific timezone
 * This correctly handles the case where the user is in a different timezone
 * than the hotel they're ordering from.
 *
 * Example: User in India (IST) selects 10:00 AM EST for a hotel in New York
 * - 10:00 AM EST = 15:00 UTC (EST is UTC-5)
 * - Result should be: 2026-02-04T15:00:00.000Z
 *
 * @param dateId - Date identifier ("today", "tomorrow", or "YYYY-MM-DD")
 * @param timeStr - Time string (e.g., "11:00 AM EST")
 * @param timezone - IANA timezone identifier of the HOTEL (e.g., "America/New_York")
 * @returns ISO string representation of the datetime in UTC
 */
export function convertToScheduledDateISO(
  dateId: string,
  timeStr: string,
  timezone: string
): string {
  // Parse the time string (e.g., "11:00 AM EST" or "10:00 AM")
  const timeWithoutTz = timeStr.replace(/\s+[A-Z]{2,5}$/, '').trim();
  const timeParts = timeWithoutTz.match(/(\d+):(\d+)\s*(AM|PM)/i);

  if (!timeParts) {
    console.error('Invalid time format:', timeStr);
    return new Date().toISOString();
  }

  let hours = parseInt(timeParts[1]);
  const minutes = parseInt(timeParts[2]);
  const meridiem = timeParts[3].toUpperCase();

  // Convert to 24-hour format
  if (meridiem === 'PM' && hours !== 12) {
    hours += 12;
  } else if (meridiem === 'AM' && hours === 12) {
    hours = 0;
  }

  // Get the target date in the hotel's timezone
  let targetDateStr: string;

  if (dateId === 'today' || dateId === 'tomorrow') {
    const daysOffset = dateId === 'today' ? 0 : 1;
    const { dateString } = getDateInTimezone(timezone, daysOffset);
    targetDateStr = dateString;
  } else {
    // dateId is already a date string like "2026-02-04"
    targetDateStr = dateId;
  }

  // Parse the date components
  const [year, month, day] = targetDateStr.split('-').map(Number);

  // Create a reference date to calculate the timezone offset
  // Use noon of the target date to avoid DST edge cases
  const referenceDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

  // Get the timezone offset for the hotel's timezone
  // This is how many minutes the timezone is behind/ahead of UTC
  const offsetMinutes = getTimezoneOffsetMinutes(timezone, referenceDate);

  // Calculate the UTC time
  // If hotel is at UTC-5 (EST), and user selected 10:00 AM:
  // - Local time in minutes from midnight: 10 * 60 + 0 = 600 minutes
  // - Offset for EST: -300 minutes (UTC-5 means we ADD 5 hours to get UTC)
  // - UTC time: 600 + 300 = 900 minutes = 15:00 UTC
  const localMinutesFromMidnight = hours * 60 + minutes;
  const utcMinutesFromMidnight = localMinutesFromMidnight + offsetMinutes;

  // Handle day overflow/underflow
  let utcDay = day;
  let utcMonth = month;
  let utcYear = year;
  let finalUtcMinutes = utcMinutesFromMidnight;

  if (utcMinutesFromMidnight >= 24 * 60) {
    // Overflow to next day
    finalUtcMinutes = utcMinutesFromMidnight - 24 * 60;
    const nextDay = new Date(Date.UTC(year, month - 1, day + 1));
    utcYear = nextDay.getUTCFullYear();
    utcMonth = nextDay.getUTCMonth() + 1;
    utcDay = nextDay.getUTCDate();
  } else if (utcMinutesFromMidnight < 0) {
    // Underflow to previous day
    finalUtcMinutes = utcMinutesFromMidnight + 24 * 60;
    const prevDay = new Date(Date.UTC(year, month - 1, day - 1));
    utcYear = prevDay.getUTCFullYear();
    utcMonth = prevDay.getUTCMonth() + 1;
    utcDay = prevDay.getUTCDate();
  }

  const utcHours = Math.floor(finalUtcMinutes / 60);
  const utcMins = finalUtcMinutes % 60;

  // Create the final UTC date
  const utcDate = new Date(Date.UTC(utcYear, utcMonth - 1, utcDay, utcHours, utcMins, 0, 0));

  console.log('Timezone conversion:', {
    input: { dateId, timeStr, timezone },
    parsed: { targetDateStr, hours, minutes },
    offset: { offsetMinutes, offsetHours: offsetMinutes / 60 },
    result: utcDate.toISOString(),
  });

  return utcDate.toISOString();
}

/**
 * Format a scheduled ISO date to display in a specific timezone
 * @param isoString - ISO date string
 * @param timezone - IANA timezone identifier
 * @returns Formatted display string
 */
export function formatScheduledDateForDisplay(
  isoString: string,
  timezone: string
): { date: string; time: string; full: string } {
  const date = new Date(isoString);
  const tzAbbr = getTimezoneAbbreviation(timezone);

  const dateOptions: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  };

  const timeOptions: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };

  const formattedDate = new Intl.DateTimeFormat('en-US', dateOptions).format(date);
  const formattedTime = new Intl.DateTimeFormat('en-US', timeOptions).format(date) + ' ' + tzAbbr;

  return {
    date: formattedDate,
    time: formattedTime,
    full: `${formattedDate}, ${formattedTime}`,
  };
}

/**
 * Check if a time is within a slot
 * @param currentMinutes - Current time in total minutes from midnight
 * @param startTime - Slot start time in HH:MM format
 * @param endTime - Slot end time in HH:MM format
 * @returns Boolean indicating if current time is within the slot
 */
export function isWithinTimeSlot(
  currentMinutes: number,
  startTime: string,
  endTime: string
): boolean {
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);
  return currentMinutes >= startMinutes && currentMinutes < endMinutes;
}

export interface TimeSlot {
  start: string;
  end: string;
}

export interface Timeline {
  day: string;
  isOpen: boolean;
  slots: TimeSlot[];
}

/**
 * Get the store open/closed status text based on hotel's timezone
 * @param timeline - Array of Timeline objects for the relevant service (pickup/delivery/store)
 * @param timezone - IANA timezone identifier
 * @returns Status text (e.g., "Open", "Closed", "Opens 9:00 AM EST")
 */
export function getStoreStatusText(
  timeline: Timeline[] | undefined,
  timezone: string
): string {
  if (!timeline) return "Closed";

  const { dayOfWeek, totalMinutes } = getCurrentTimeInTimezone(timezone);
  const todaySchedule = timeline.find((t) => t.day === dayOfWeek);

  if (!todaySchedule || !todaySchedule.isOpen || todaySchedule.slots.length === 0) {
    return "Closed";
  }

  // Check if currently open
  for (const slot of todaySchedule.slots) {
    if (isWithinTimeSlot(totalMinutes, slot.start, slot.end)) {
      return "Open";
    }
  }

  // Not currently open - check if will open later today
  const firstSlot = todaySchedule.slots[0];
  const firstSlotStart = timeToMinutes(firstSlot.start);

  // If before first opening time
  if (totalMinutes < firstSlotStart) {
    return `Opens ${formatTimeWithTimezone(firstSlot.start, timezone)}`;
  }

  // Check for next slot today
  for (const slot of todaySchedule.slots) {
    const slotStart = timeToMinutes(slot.start);
    if (totalMinutes < slotStart) {
      return `Opens ${formatTimeWithTimezone(slot.start, timezone)}`;
    }
  }

  // After all slots closed
  return "Closed";
}

/**
 * Check if currently open based on timeline and timezone
 * @param timeline - Array of Timeline objects
 * @param timezone - IANA timezone identifier
 * @returns Boolean and next open time if closed
 */
export function checkStoreOpenStatus(
  timeline: Timeline[] | undefined,
  timezone: string
): StoreStatus {
  if (!timeline) return { isOpen: false, nextOpenTime: "Closed" };

  const { dayOfWeek, totalMinutes } = getCurrentTimeInTimezone(timezone);
  const todaySchedule = timeline.find((t) => t.day === dayOfWeek);

  if (!todaySchedule || !todaySchedule.isOpen || todaySchedule.slots.length === 0) {
    return { isOpen: false, nextOpenTime: "Closed today" };
  }

  // Check if currently open
  for (const slot of todaySchedule.slots) {
    if (isWithinTimeSlot(totalMinutes, slot.start, slot.end)) {
      return { isOpen: true };
    }
  }

  // Get next opening time
  const firstSlot = todaySchedule.slots[0];
  const nextOpenTime = `Opens ${formatTimeWithTimezone(firstSlot.start, timezone)}`;

  return { isOpen: false, nextOpenTime };
}

/**
 * Generate time slots for a given day based on timeline and timezone
 * @param timeline - Array of Timeline objects
 * @param dayOfWeek - Day name (e.g., "Monday")
 * @param timezone - IANA timezone identifier
 * @param isToday - Whether this is for today (to filter past times)
 * @param interval - Interval in minutes between slots (default: 15)
 * @returns Array of formatted time slot strings
 */
export function generateTimeSlotsForDay(
  timeline: Timeline[] | undefined,
  dayOfWeek: string,
  timezone: string,
  isToday: boolean = false,
  interval: number = 15
): string[] {
  if (!timeline) return [];

  const daySchedule = timeline.find((t) => t.day === dayOfWeek);

  if (!daySchedule || !daySchedule.isOpen || daySchedule.slots.length === 0) {
    return [];
  }

  const slots: string[] = [];
  const tzAbbr = getTimezoneAbbreviation(timezone);
  const currentTime = isToday ? getCurrentTimeInTimezone(timezone) : null;

  for (const slot of daySchedule.slots) {
    const [startHour, startMin] = slot.start.split(":").map(Number);
    const [endHour, endMin] = slot.end.split(":").map(Number);

    let currentMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;

    while (currentMinutes <= endMinutes) {
      // Skip past times if today
      if (!isToday || !currentTime || currentMinutes > currentTime.totalMinutes) {
        const hours = Math.floor(currentMinutes / 60);
        const mins = currentMinutes % 60;
        const period = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        slots.push(`${displayHours}:${mins.toString().padStart(2, '0')} ${period} ${tzAbbr}`);
      }
      currentMinutes += interval;
    }
  }

  return slots;
}

/**
 * Persist / read a restaurant's ASAP delay (minutes) so ASAP prep estimates
 * survive a page reload, when the delivery context (and the restaurant object
 * carrying asapDelay) is empty. Keyed by restaurant id or order id.
 */
const asapDelayKey = (key: string) => `asap_delay:${key}`;

export function saveAsapDelay(key: string | undefined | null, delay: number | undefined | null) {
  if (!key || typeof delay !== "number") return;
  try {
    localStorage.setItem(asapDelayKey(key), String(delay));
  } catch {
    /* storage unavailable */
  }
}

export function loadAsapDelay(key: string | undefined | null): number | undefined {
  if (!key) return undefined;
  try {
    const raw = localStorage.getItem(asapDelayKey(key));
    if (raw === null) return undefined;
    const value = Number(raw);
    return Number.isFinite(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Generate an ASAP scheduled time in a specific timezone
 * @param timezone - IANA timezone identifier
 * @param delayMinutes - minutes to add to current time (default 0)
 * @returns Object with ISO string and display time
 */
export function getAsapScheduledTime(timezone: string, delayMinutes: number = 0): {
  isoString: string;
  displayTime: string;
  displayDate: string;
  delayMinutes: number;
} {
  const now = new Date();
  const totalDelay = Math.max(0, delayMinutes);
  const asapTime = new Date(now.getTime() + totalDelay * 60 * 1000);

  const tzAbbr = getTimezoneAbbreviation(timezone);

  // Format the display time in the hotel's timezone
  const timeOptions: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };

  const displayTime = new Intl.DateTimeFormat('en-US', timeOptions).format(asapTime) + ' ' + tzAbbr;

  return {
    isoString: asapTime.toISOString(),
    displayTime,
    displayDate: 'today',
    delayMinutes: totalDelay,
  };
}

/**
 * Check if restaurant is currently open for a specific order type
 * @param location - Location object with timeline data
 * @param orderType - 'pickup' or 'delivery'
 * @returns Boolean indicating if currently open
 */
export function isRestaurantCurrentlyOpen(
  location: {
    pickupTimeline?: Timeline[];
    deliveryTimeline?: Timeline[];
    timezone?: string;
    _timezoneInfo?: { id: string };
  } | null | undefined,
  orderType: 'pickup' | 'delivery'
): boolean {
  if (!location) return false;

  const timezone = location.timezone || location._timezoneInfo?.id || 'America/Denver';
  const timeline = orderType === 'pickup' ? location.pickupTimeline : location.deliveryTimeline;

  if (!timeline) return false;

  const { isOpen } = checkStoreOpenStatus(timeline, timezone);
  return isOpen;
}

export const BIRTHDAY_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Days per month for a birthday (no year, so February allows the 29th)
export const BIRTHDAY_MONTH_DAYS = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** "March 14" for a { month, day } birthday */
export function formatBirthday(birthday?: { month: number; day: number } | null): string {
  if (!birthday?.month || !birthday?.day) return "";
  return `${BIRTHDAY_MONTHS[birthday.month - 1]} ${birthday.day}`;
}
